import { ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { Button, type ButtonVariant } from './Button';

/**
 * EmptyState — vista de ausencia con ilustracion + copy + CTA opcional
 * (DESIGN-SYSTEM §3.21).
 *
 * Composicion:
 *  - `illustration`: nodo React (ej. `<LunaIcon size={64} />`) renderizado en
 *    `cream-200` por defecto. El consumidor controla el color final via
 *    Tailwind. La ilustracion va `aria-hidden` (el copy lo describe).
 *  - `title` y `description`: copy en espanol, especifico al contexto
 *    (no "no hay datos").
 *  - `action`: CTA primaria opcional `{ label, onClick, variant? }`.
 *
 * ARIA: `role="region"` + `aria-label` (usa `title` como fallback).
 */
export interface EmptyStateAction {
  label: string;
  onClick: () => void;
  variant?: ButtonVariant;
}

export interface EmptyStateProps {
  /** Ilustracion / icono decorativo. */
  illustration?: ReactNode;
  title: string;
  description?: string;
  action?: EmptyStateAction;
  /** Aria-label explicito. Por defecto usa `title`. */
  'aria-label'?: string;
  className?: string;
}

export function EmptyState({
  illustration,
  title,
  description,
  action,
  className,
  'aria-label': ariaLabel,
}: EmptyStateProps) {
  return (
    <section
      role="region"
      aria-label={ariaLabel ?? title}
      className={cn(
        'flex flex-col items-center justify-center text-center',
        'py-12 px-6 gap-4',
        className,
      )}
    >
      {illustration && (
        <div className="text-primary-200" aria-hidden="true">
          {illustration}
        </div>
      )}
      <div className="max-w-md space-y-2">
        <h2 className="font-display text-h3 text-primary-900">{title}</h2>
        {description && (
          <p className="text-body text-primary-900/70">{description}</p>
        )}
      </div>
      {action && (
        <div className="mt-2">
          <Button
            variant={action.variant ?? 'primary'}
            size="md"
            onClick={action.onClick}
          >
            {action.label}
          </Button>
        </div>
      )}
    </section>
  );
}
